import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, CircularProgress, Tab, Tabs, Theme, Typography, alpha } from "@mui/material";
import { ArrowBack, ErrorOutline } from "@mui/icons-material";

import api from "services/api";

import { IResponse, IUploadHistory } from "./Governance.types";
import { Title } from "./Governance.styled";
import { GovernanceActivity } from "./GovernanceActivity";
import { GovernanceUploadDetail } from "./GovernanceUploadDetail";
import { GovernanceHistoryList } from "./GovernanceHistory.List";
import { GovernanceUploadButton } from "./GovernanceUploadButton";

type GovernanceProps = {
  organizationId: string;
  /** Sai da Governance e volta para a lista de atividades. */
  onBack: () => void;
};

/** Pill: sem indicador embaixo, fundo do primary a 8% e texto do primary quando selecionada. */
const tabSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "13px",
  minHeight: "unset",
  minWidth: "unset",
  padding: "8px 16px",
  borderRadius: "8px",
  color: "text.primary",
  "&.Mui-selected": {
    color: "primary.main",
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
  },
} as const;

export function Governance({ organizationId, onBack }: GovernanceProps) {
  const [tab, setTab] = useState(0);
  /**
   * Mora aqui, e não no histórico, porque selecionar um upload troca o cabeçalho e esconde as
   * tabs — coisas que este componente renderiza.
   */
  const [selectedUpload, setSelectedUpload] = useState<IUploadHistory | null>(null);
  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["governance-upload-history", organizationId],
    queryFn: () => api.get<{ data: IResponse }>(`/v2/governance/${organizationId}`),
    select: (data) => data.data.data,
  });

  /**
   * Pelo `createdAt`, não por `data[0]`: o backend hoje ordena desc, mas depender dessa ordem
   * faria a aba Atividade mostrar o upload errado se o `orderBy` mudasse.
   */
  const latestUpload = useMemo(() => {
    if (!data?.length) return undefined;

    return data.reduce((latest, upload) =>
      new Date(upload.createdAt).getTime() > new Date(latest.createdAt).getTime() ? upload : latest
    );
  }, [data]);

  /**
   * O cabeçalho fica fora disto e é renderizado sempre: se o corpo levar um early return, o
   * usuário perde a seta de voltar e fica preso na tela — foi o que acontecia sem histórico.
   */
  function renderBody() {
    if (!isFetched && isFetching) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3}>
          <CircularProgress size={30} thickness={4} />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3} textAlign="center">
          <ErrorOutline sx={{ fontSize: 40, color: "error.main" }} />
          <Typography variant="h6" color="textPrimary">
            Ops! Algo deu errado
          </Typography>
          <Typography variant="body1" color="textSecondary" maxWidth={400}>
            Não foi possível carregar o histórico de uploads no momento. Por favor, tente novamente.
          </Typography>
        </Box>
      );
    }

    if (selectedUpload) return <GovernanceUploadDetail upload={selectedUpload} />;

    return (
      <>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          TabIndicatorProps={{ sx: { display: "none" } }}
          sx={{
            minHeight: "unset",
            marginBottom: "24px",
            "& .MuiTabs-flexContainer": { gap: "8px" },
          }}
        >
          <Tab label="Atividade" sx={tabSx} />
          <Tab label="Histórico de carregamentos" sx={tabSx} />
        </Tabs>

        {/* `latestUpload` é undefined antes da primeira carga: a tela é a mesma, só vazia. */}
        {tab === 0 && <GovernanceActivity organizationId={organizationId} upload={latestUpload} />}
        {tab === 1 && <GovernanceHistoryList uploads={data ?? []} onSelect={setSelectedUpload} />}
      </>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ marginBottom: "16px" }}>
        {selectedUpload ? (
          <div style={{ display: "flex" }}>
            <Button startIcon={<ArrowBack />} onClick={() => setSelectedUpload(null)} sx={{ textTransform: "none" }} />
            <Title>Histórico de carregamentos</Title>
          </div>
        ) : (
          <div style={{ display: "flex" }}>
            <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: "none" }} />
            <Title>Carregar beneficiários manualmente</Title>
          </div>
        )}
      </Box>

      {renderBody()}
    </Box>
  );
}
